import { fromEventPattern, map, Observable, takeWhile } from 'rxjs';
import { MatrixEvent, MatrixEventEvent } from 'matrix-js-sdk';

interface MatrixEventEmitter {
  on(event: string, listener: (...args: unknown[]) => void): void;

  removeListener(event: string, listener: (...args: unknown[]) => void): void;
}

// Overload 1: Single argument
export function fromMatrixEvent<A>(emitter: MatrixEventEmitter, event: string): Observable<A>;

// Overload 2: Multiple arguments as tuple
export function fromMatrixEvent<A extends unknown[]>(emitter: MatrixEventEmitter, event: string): Observable<A>;

/**
 * Baut ein Observable aus Matrix-Events.
 * Utility-Methode entscheidet automatisch:
 * Wenn das Event nur ein Argument liefert → Observable<arg>
 * Wenn das Event mehrere Argumente liefert → Observable<[arg1, arg2, …]>
 * Das geht mit TypeScript-Overloads.
 */
export function fromMatrixEvent(emitter: MatrixEventEmitter, event: string): Observable<unknown> {
  return fromEventPattern<unknown[]>(
    handler => emitter.on(event, handler),
    handler => emitter.removeListener(event, handler)
  ).pipe(
    // Wenn nur 1 Argument, dieses extrahieren.
    // Sonst das gesamte Array zurückgeben.
    map(args => (args.length === 1 ? args[0] : args))
  );
}

/**
 * Observable, das bei jeder (Re-)Entschlüsselung eines Events feuert und das Event zurückgibt.
 *
 * matrix-js-sdk feuert {@link MatrixEventEvent.Decrypted} auch bei fehlgeschlagener Entschlüsselung
 * und ERNEUT, sobald der Megolm-Key verspätet eintrifft (typisch bei Föderation/Gruppenräumen).
 * Daher wird NICHT beim ersten – ggf. fehlgeschlagenen – Versuch gestoppt (kein `take(1)`), sondern
 * bis zur erfolgreichen Entschlüsselung weitergelauscht (`takeWhile(..., inclusive)`); danach ist
 * kein weiteres Decrypted mehr zu erwarten und das Observable schließt ab. Sonst bliebe eine
 * zunächst nicht entschlüsselbare Nachricht dauerhaft auf "Unable to decrypt".
 */
export function decryptionUpdates$(event: MatrixEvent): Observable<MatrixEvent> {
  return fromMatrixEvent<MatrixEvent>(event, MatrixEventEvent.Decrypted).pipe(
    takeWhile(() => event.isDecryptionFailure(), true),
    map(() => event)
  );
}
